<template>
  <button
    ref="button"
    class="processing-button"
    type="button"
    :disabled="disabled"
    :class="[variant, { submitted: submitted, error: error, loading: loading }]"
    :style="{'min-width': minWidth + 'px'}"
    @click="onClick">
    <transition @enter="enter"
                @leave="leave">
      <template v-if="loading">
        <slot name="loading">
          <font-awesome-icon key="foo"
                             icon="spinner"
                             aria-hidden="true"
                             spin></font-awesome-icon>
        </slot>
      </template>
      <template v-else>
        <template v-if="!submitted">
          <span>
            <slot name="default"></slot>
          </span>
        </template>
        <template v-else>
          <template v-if="error">
            <slot name="error">
              <font-awesome-icon
                key="error"
                aria-hidden="true"
                class="icon"
                :icon="['fas', 'times']">
              </font-awesome-icon>
            </slot>
          </template>
          <template v-else>
            <slot name="success">
              <font-awesome-icon key="success"
                                 aria-hidden="true"
                                 icon="check">
              </font-awesome-icon>
            </slot>
          </template>
        </template>
      </template>
    </transition>
  </button>
</template>

<script>
import gsap from "gsap";

export default {
  name: "ProcessingButton",
  props: {
    variant: {
      type: String,
      default: null,
    },
    action: {
      type: Function,
      default: null,
    },
    actionParams: {
      type: Array,
      default: () => [],
    },
    resultTime: {
      type: Number,
      default: 1750,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    triggerAction: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      loading: false,
      submitted: false,
      error: false,
      minWidth: 100,
    };
  },
  watch: {
    triggerAction ( val ) {
      if ( val ) {
        this.onClick();
      }
    },
  },
  mounted () {
    window.setTimeout( () => {
      this.calculateButtonWidth();
    }, 100 );
  },
  methods: {
    calculateButtonWidth () {
      const width = this.$refs.button.clientWidth;
      if ( width !== 0 ) {
        this.minWidth = width + 2; // plus two for the border on the button
      }
    },
    onClick () {
      if ( this.action && typeof this.action === "function" ) {
        if ( !this.loading && !this.submitted ) {
          this.loading = true;
          this.submitted = false;
          this.error = false;
          this.action( ...this.actionParams )
            .then( ( response ) => {
              this.$emit( "response", response );
            } )
            .catch( () => {
              this.error = true;
            } )
            .finally( () => {
              this.loading = false;
              this.submitted = true;
              window.setTimeout( () => {
                this.submitted = false;
                this.error = false;
              }, this.resultTime );
            } );
        }
      }
    },
    enter ( el, done ) {
      el.style.opacity = 0;
      el.style.position = "absolute";
      gsap.killTweensOf( el );

      gsap.to( el, {
        duration: 0.25,
        opacity: 1,
        onComplete: () => {
          el.style.position = "relative";
          done();
        },
      } );
    },
    leave ( el, done ) {
      gsap.killTweensOf( el );

      gsap.to( el, {
        duration: 0.25,
        opacity: 0,
        onComplete: done,
      } );
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors";

.processing-button {
  display: flex;
  justify-content: center;
  width: unset;
  min-width: unset;
  color: @color-primary;
  background-color: @color-secondary;

  &:hover {
    color: @color-primary;
    background-color: tint(@color-secondary, 30%);
  }

  svg {
    color: @color-primary;
    margin: 0 !important;
  }
}
</style>
